import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  forceCenter,
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
  forceX,
  forceY,
  type Simulation,
  type SimulationLinkDatum,
  type SimulationNodeDatum,
} from "d3-force";
import {
  hierarchyToNetworkGraph,
  TOPIC_CHART_COLORS,
  type DominanceLevel,
  type NetworkNode,
  type TopicHierarchyNode,
} from "./taxonomyHierarchy";

type SimNode = NetworkNode &
  SimulationNodeDatum & {
    x: number;
    y: number;
    radius: number;
  };

type SimLink = SimulationLinkDatum<SimNode> & {
  source: SimNode;
  target: SimNode;
};

const BOUNDARY_PAD = 12;
const LABEL_HEIGHT = 14;
const LABEL_WIDTH_HALF = 48;

type NodeBounds = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

function nodeExtents(node: SimNode, showLabel: boolean): NodeBounds {
  const labelBelow = showLabel ? LABEL_HEIGHT + 10 : 0;
  const labelAbove = showLabel ? 12 : 0;
  const labelSide = showLabel ? LABEL_WIDTH_HALF : 0;

  return {
    minX: node.x - node.radius - labelSide,
    minY: node.y - node.radius - labelAbove,
    maxX: node.x + node.radius + labelSide,
    maxY: node.y + node.radius + labelBelow,
  };
}

function clampNodePosition(
  node: SimNode,
  boxWidth: number,
  boxHeight: number,
  showLabel: boolean,
): void {
  const labelBelow = showLabel ? LABEL_HEIGHT + 8 : 0;
  const labelAbove = showLabel ? 10 : 0;
  const labelSide = showLabel ? LABEL_WIDTH_HALF : 0;

  const minX = node.radius + BOUNDARY_PAD + labelSide;
  const maxX = boxWidth - node.radius - BOUNDARY_PAD - labelSide;
  const minY = node.radius + BOUNDARY_PAD + labelAbove;
  const maxY = boxHeight - node.radius - BOUNDARY_PAD - labelBelow;

  if (typeof node.x === "number") {
    node.x = Math.max(minX, Math.min(maxX, node.x));
  }
  if (typeof node.y === "number") {
    node.y = Math.max(minY, Math.min(maxY, node.y));
  }
}

function clampFixedPosition(
  x: number,
  y: number,
  radius: number,
  boxWidth: number,
  boxHeight: number,
  showLabel: boolean,
): { x: number; y: number } {
  const node = { x, y, radius } as SimNode;
  clampNodePosition(node, boxWidth, boxHeight, showLabel);
  return { x: node.x, y: node.y };
}

function fitNodesInBox(
  nodes: SimNode[],
  boxWidth: number,
  boxHeight: number,
  labelNodeIds: Set<string>,
): void {
  if (nodes.length === 0) return;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const node of nodes) {
    const ext = nodeExtents(node, labelNodeIds.has(node.id));
    minX = Math.min(minX, ext.minX);
    minY = Math.min(minY, ext.minY);
    maxX = Math.max(maxX, ext.maxX);
    maxY = Math.max(maxY, ext.maxY);
  }

  const graphWidth = maxX - minX;
  const graphHeight = maxY - minY;
  if (graphWidth <= 0 || graphHeight <= 0) return;

  const availableWidth = boxWidth - BOUNDARY_PAD * 2;
  const availableHeight = boxHeight - BOUNDARY_PAD * 2;
  const scale = Math.min(1, availableWidth / graphWidth, availableHeight / graphHeight);
  const graphCx = (minX + maxX) / 2;
  const graphCy = (minY + maxY) / 2;

  for (const node of nodes) {
    node.x = boxWidth / 2 + (node.x - graphCx) * scale;
    node.y = boxHeight / 2 + (node.y - graphCy) * scale;
    clampNodePosition(node, boxWidth, boxHeight, labelNodeIds.has(node.id));
  }
}

function nodeRadius(node: NetworkNode, maxMentions: number): number {
  const min = node.kind === "topic" ? 14 : 8;
  const max = node.kind === "topic" ? 36 : 22;
  if (maxMentions <= 0) return min;
  const t = node.mentions / maxMentions;
  return min + t * (max - min);
}

function maxLinkDistance(width: number, height: number): number {
  return Math.min(width, height) * 0.14;
}

function initializeSubtopicLayout(
  nodes: SimNode[],
  width: number,
  height: number,
): void {
  const topicNodes = nodes.filter((node) => node.kind === "topic");
  const cx = width / 2;
  const cy = height / 2;
  const topicOrbit =
    Math.min(width, height) *
    Math.min(0.18, 0.42 / Math.max(Math.sqrt(topicNodes.length), 1));

  topicNodes.forEach((topic, index) => {
    const angle = (Math.PI * 2 * index) / Math.max(topicNodes.length, 1) - Math.PI / 2;
    topic.x = cx + Math.cos(angle) * topicOrbit;
    topic.y = cy + Math.sin(angle) * topicOrbit;
  });

  const subsByTopic = new Map<string, SimNode[]>();
  for (const sub of nodes.filter((node) => node.kind === "subtopic")) {
    const parentSlug = sub.parentTopicSlug ?? "";
    const bucket = subsByTopic.get(parentSlug) ?? [];
    bucket.push(sub);
    subsByTopic.set(parentSlug, bucket);
  }

  const maxDistance = maxLinkDistance(width, height);

  for (const [parentSlug, subs] of Array.from(subsByTopic.entries())) {
    const parent = topicNodes.find((topic) => topic.slug === parentSlug);
    if (!parent) continue;

    subs.forEach((sub: SimNode, index: number) => {
      const spread = Math.min(Math.PI * 0.85, Math.PI / 2 + subs.length * 0.35);
      const start = -spread / 2;
      const step = subs.length <= 1 ? 0 : spread / (subs.length - 1);
      const angle = start + step * index - Math.PI / 2;
      const distance = Math.min(parent.radius + sub.radius + 28, maxDistance);
      sub.x = parent.x + Math.cos(angle) * distance;
      sub.y = parent.y + Math.sin(angle) * distance;
    });
  }
}

function initializeTopicLayout(nodes: SimNode[], width: number, height: number): void {
  const cx = width / 2;
  const cy = height / 2;
  const orbit =
    Math.min(width, height) *
    Math.min(0.24, 0.5 / Math.max(Math.sqrt(nodes.length), 1));

  nodes.forEach((node, index) => {
    const angle = (Math.PI * 2 * index) / Math.max(nodes.length, 1) - Math.PI / 2;
    node.x = cx + Math.cos(angle) * orbit;
    node.y = cy + Math.sin(angle) * orbit;
  });
}

type TopicNetworkGraphProps = {
  hierarchy: TopicHierarchyNode[];
  level?: DominanceLevel;
  height?: number;
};

const TopicNetworkGraph: React.FC<TopicNetworkGraphProps> = ({
  hierarchy,
  level = "topic",
  height = 420,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const simulationRef = useRef<Simulation<SimNode, SimLink> | null>(null);
  const dragNodeRef = useRef<string | null>(null);
  const [width, setWidth] = useState(0);

  const graph = useMemo(() => hierarchyToNetworkGraph(hierarchy, level), [hierarchy, level]);
  const maxMentions = useMemo(
    () => Math.max(1, ...graph.nodes.map((n) => n.mentions)),
    [graph.nodes],
  );

  const [nodes, setNodes] = useState<SimNode[]>([]);
  const [links, setLinks] = useState<SimLink[]>([]);

  const labelNodeIds = useMemo(
    () =>
      new Set(
        [...graph.nodes]
          .sort((a, b) => b.mentions - a.mentions)
          .slice(0, 12)
          .map((node) => node.id),
      ),
    [graph.nodes],
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width;
      if (next && next > 0) setWidth(next);
    });
    observer.observe(el);
    setWidth(el.clientWidth || 0);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (width <= 0 || graph.nodes.length === 0) {
      setNodes([]);
      setLinks([]);
      return;
    }

    const initialNodes: SimNode[] = graph.nodes.map((node) => ({
      ...node,
      x: width / 2,
      y: height / 2,
      radius: nodeRadius(node, maxMentions),
    }));

    const nodeById = new Map(initialNodes.map((node) => [node.id, node]));
    const initialLinks: SimLink[] = [];
    for (const link of graph.links) {
      const source = nodeById.get(link.source);
      const target = nodeById.get(link.target);
      if (!source || !target) continue;
      initialLinks.push({ ...link, source, target });
    }

    if (level === "subtopic") {
      initializeSubtopicLayout(initialNodes, width, height);
    } else {
      initializeTopicLayout(initialNodes, width, height);
    }

    fitNodesInBox(initialNodes, width, height, labelNodeIds);

    for (const node of initialNodes) {
      clampNodePosition(node, width, height, labelNodeIds.has(node.id));
    }

    const linkDistanceCap = maxLinkDistance(width, height);
    const chargeStrength =
      level === "subtopic"
        ? Math.max(-90, -320 / Math.sqrt(initialNodes.length))
        : Math.max(-120, -280 / Math.sqrt(initialNodes.length));

    simulationRef.current?.stop();

    const simulation = forceSimulation<SimNode>(initialNodes)
      .force(
        "link",
        forceLink<SimNode, SimLink>(initialLinks)
          .id((node) => node.id)
          .distance((link) => {
            const target = link.target as SimNode;
            const source = link.source as SimNode;
            if (source.kind === "topic" && target.kind === "subtopic") {
              return Math.min(source.radius + target.radius + 32, linkDistanceCap);
            }
            return Math.min(80, linkDistanceCap);
          })
          .strength(0.85),
      )
      .force("charge", forceManyBody().strength(chargeStrength))
      .force("center", forceCenter(width / 2, height / 2))
      .force("x", forceX(width / 2).strength(0.12))
      .force("y", forceY(height / 2).strength(0.12))
      .force(
        "collide",
        forceCollide<SimNode>().radius((node) => node.radius + (labelNodeIds.has(node.id) ? 10 : 6)),
      )
      .alpha(0.85)
      .alphaDecay(0.06)
      .velocityDecay(0.45)
      .on("tick", () => {
        for (const node of simulation.nodes()) {
          clampNodePosition(node, width, height, labelNodeIds.has(node.id));
        }
        setNodes([...simulation.nodes()]);
        setLinks([...initialLinks]);
      })
      .on("end", () => {
        fitNodesInBox(simulation.nodes(), width, height, labelNodeIds);
        setNodes([...simulation.nodes()]);
      });

    simulationRef.current = simulation;

    return () => {
      simulation.stop();
    };
  }, [graph, width, height, maxMentions, level, labelNodeIds]);

  const handlePointerDown = (nodeId: string) => {
    dragNodeRef.current = nodeId;
    const sim = simulationRef.current;
    if (!sim) return;
    const node = sim.nodes().find((n) => n.id === nodeId);
    if (!node) return;
    node.fx = node.x;
    node.fy = node.y;
    sim.alphaTarget(0.3).restart();
  };

  const handlePointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
    const nodeId = dragNodeRef.current;
    const sim = simulationRef.current;
    if (!nodeId || !sim) return;

    const svg = event.currentTarget;
    const point = svg.createSVGPoint();
    point.x = event.clientX;
    point.y = event.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return;
    const local = point.matrixTransform(ctm.inverse());

    const node = sim.nodes().find((n) => n.id === nodeId);
    if (!node) return;
    const clamped = clampFixedPosition(
      local.x,
      local.y,
      node.radius,
      width,
      height,
      labelNodeIds.has(node.id),
    );
    node.fx = clamped.x;
    node.fy = clamped.y;
    node.x = clamped.x;
    node.y = clamped.y;
  };

  const handlePointerUp = () => {
    const nodeId = dragNodeRef.current;
    dragNodeRef.current = null;
    const sim = simulationRef.current;
    if (!sim) return;
    if (nodeId) {
      const node = sim.nodes().find((n) => n.id === nodeId);
      if (node) {
        node.fx = null;
        node.fy = null;
      }
    }
    sim.alphaTarget(0);
  };

  if (graph.nodes.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {level === "subtopic"
          ? "No subtopic assignments to visualize."
          : "No accepted topic assignments to visualize."}
      </Typography>
    );
  }

  const clipId = `network-clip-${level}`;

  return (
    <Box
      ref={containerRef}
      sx={{
        width: "100%",
        height,
        overflow: "hidden",
        borderRadius: 1,
        bgcolor: "action.hover",
      }}
    >
      {width > 0 && (
        <svg
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label="Topic network graph"
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          style={{ touchAction: "none", display: "block" }}
        >
          <defs>
            <clipPath id={clipId}>
              <rect x={0} y={0} width={width} height={height} rx={4} />
            </clipPath>
          </defs>
          <g clipPath={`url(#${clipId})`}>
            {links.map((link) => {
              const source = link.source as SimNode;
              const target = link.target as SimNode;
              const topicColor =
                TOPIC_CHART_COLORS[source.colorIndex % TOPIC_CHART_COLORS.length] ?? "#8884d8";
              return (
                <line
                  key={`${source.id}-${target.id}`}
                  x1={source.x}
                  y1={source.y}
                  x2={target.x}
                  y2={target.y}
                  stroke={topicColor}
                  strokeWidth={1.5}
                  strokeOpacity={0.55}
                />
              );
            })}
            {nodes.map((node) => {
              const fill = TOPIC_CHART_COLORS[node.colorIndex % TOPIC_CHART_COLORS.length] ?? "#8884d8";
              const subFill = adjustColor(fill, 0.45);
              const showLabel = labelNodeIds.has(node.id);
              const labelBelowY = node.y + node.radius + 12;
              const labelAbove = labelBelowY + LABEL_HEIGHT > height - BOUNDARY_PAD - 4;
              const labelY = labelAbove ? node.y - node.radius - 6 : labelBelowY;
              const labelText =
                node.display.length > 16 ? `${node.display.slice(0, 16)}…` : node.display;

              return (
                <g key={node.id}>
                  <circle
                    cx={node.x}
                    cy={node.y}
                    r={node.radius}
                    fill={node.kind === "topic" ? fill : subFill}
                    stroke={fill}
                    strokeWidth={node.kind === "topic" ? 0 : 2}
                    opacity={0.92}
                    style={{ cursor: "grab" }}
                    onPointerDown={(event) => {
                      event.currentTarget.setPointerCapture(event.pointerId);
                      handlePointerDown(node.id);
                    }}
                  >
                    <title>
                      {node.display} ({node.slug}) — {node.mentions} chunk
                      {node.mentions === 1 ? "" : "s"}
                    </title>
                  </circle>
                  {showLabel && (
                    <text
                      x={node.x}
                      y={labelY}
                      textAnchor="middle"
                      fontSize={10}
                      fill="#424242"
                      pointerEvents="none"
                    >
                      {labelText}
                    </text>
                  )}
                </g>
              );
            })}
          </g>
        </svg>
      )}
    </Box>
  );
};

function adjustColor(hex: string, lighten: number): string {
  const normalized = hex.replace("#", "");
  if (normalized.length !== 6) return hex;
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  const mix = (channel: number) =>
    Math.round(channel + (255 - channel) * Math.min(1, Math.max(0, lighten)));
  return `#${mix(r).toString(16).padStart(2, "0")}${mix(g).toString(16).padStart(2, "0")}${mix(b).toString(16).padStart(2, "0")}`;
}

export default TopicNetworkGraph;
